/**
 * RoomInvitationReceived (PPL-26).
 *
 * The invitee's authenticated "received" page. Replaces the PPL-17 stub at
 * `/rooms/:mediationId/invite`.
 *
 * Design invariant — THREE EQUAL-WEIGHT RESPONSES (matches integration-web C1):
 *   accept   ·   tell me more before i decide   ·   decline
 * No accent-button bias toward accept. All three buttons share the same
 * visual weight (same height, padding, font-size); only an outline / border
 * tint differentiates them, never primary/secondary hierarchy.
 *
 * Visual shell: the F8Chat CSS classes (`f8-chat-shell`, `f8-conversation`,
 * `f8-agent-bubble`, etc.) — NOT the `useF8Chat` hook. The hook would force
 * `/login` on send AND write into the invitee's private chatThreads
 * collection — both wrong for this surface. Instead, we render a scripted
 * read-only projection: f8 narrates the invitation in two bubbles, plus a
 * third "tell me more" bubble that appears locally when the invitee asks
 * for it (no callable, no state advance).
 *
 * Composer: the F8Chat composer block is rendered in DISABLED form (textarea
 * locked, send button inactive) — this is the "pre-signup composer
 * disabled" pattern called out in the spec. There is no free-text channel on
 * this surface; the only inputs are the three response buttons.
 *
 * Calls `respondToRoomInvite(mediationId, response)` for accept /
 * decline. The middle "tell me more" response surfaces a local f8 follow-up
 * and does NOT advance state.
 */

import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowUp, Mic } from 'react-feather';
import { respondToRoomInvite, getMediationInviter } from '../services/roomService';
import '../styles/F8Chat.css';
import '../styles/RoomInvitationReceived.css';

// ─── Scripted projection ─────────────────────────────────────────────────────
// f8 narrates the invitation. The invitee's "tell me more" tap appends a
// fourth bubble (a local f8 reply); it does NOT call the server.

interface ScriptMsg {
  id: string;
  sender: 'bot' | 'user';
  text: string;
}

function buildScript(opts: { inviterName: string | null; reason: string }): ScriptMsg[] {
  const who = opts.inviterName || 'someone';
  const turns: ScriptMsg[] = [
    {
      id: 'f8-1',
      sender: 'bot',
      text: `hey. ${who} wants to open a room with you.`,
    },
    {
      id: 'f8-2',
      sender: 'bot',
      text: opts.reason
        ? `they wrote: "${opts.reason.trim()}"`
        : 'they did not add a reason.',
    },
    {
      id: 'f8-3',
      sender: 'bot',
      text: 'you have three options. accept and i will walk us both through prep first. ask for more before deciding. or decline — that ends here, on your terms.',
    },
  ];
  return turns;
}

const TELL_ME_MORE_USER: ScriptMsg = {
  id: 'me-tellmemore',
  sender: 'user',
  text: 'tell me more before i decide',
};

const TELL_ME_MORE_F8: ScriptMsg = {
  id: 'f8-tellmemore',
  sender: 'bot',
  text:
    'okay. if you accept, you and they each do prep with me first — i ask questions, you say what matters, no one sees the other side until you both opt in. then we meet in a shared room with sharing rules you both set. you can pause or end anytime. nothing surfaces without your permission.',
};

type ResponseId = 'accept' | 'tell_me_more' | 'decline';

interface ResponseDef {
  id: ResponseId;
  label: string;
}

const RESPONSES: ResponseDef[] = [
  { id: 'accept', label: 'accept' },
  { id: 'tell_me_more', label: 'tell me more before i decide' },
  { id: 'decline', label: 'decline' },
];

interface LocationStateProps {
  inviterName?: string | null;
  inviterHandle?: string | null;
  reason?: string | null;
}

const RoomInvitationReceived: React.FC = () => {
  const { mediationId } = useParams<{ mediationId: string }>();
  const navigate = useNavigate();

  // Inviter name + reason ride through router state (set by the navigator
  // that brought the user here — typically the f8 notification card). If
  // missing, fall back to a server fetch so the copy degrades gracefully on
  // refresh or direct URL.
  const routerState = (window.history.state?.usr || {}) as LocationStateProps;
  const routerInviterName = routerState.inviterName || routerState.inviterHandle || null;
  const routerReason = (routerState.reason || '').toString();

  const [inviterName, setInviterName] = useState<string | null>(routerInviterName);
  const [reason, setReason] = useState<string>(routerReason);

  // When router state is absent (e.g. navigated from the rooms list, not the
  // f8 notification card), resolve the inviter via the mediation-scoped
  // callable — it returns firstName + tetherHandle + reason in one auth'd
  // round-trip, so we get "Cam" (firstName) instead of "@SunsetMediator".
  // We deliberately do NOT call `getPublicProfile` here: that callable is
  // handle-only by design (privacy on profiles / search / forum), and falling
  // back to it leaks "@handle" into the receive copy. The dedicated callable
  // is auth-gated on mediation participation, so firstName exposure stays
  // scoped to the two people who already opted into this room.
  //
  // Why no `useRef` "one-shot" flag: React 18 StrictMode double-invokes the
  // effect — a ref-gated subscribe leaves the second mount with no live
  // subscription and the script stuck on "someone" / "no reason".
  useEffect(() => {
    if (routerInviterName || !mediationId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getMediationInviter(mediationId);
        if (cancelled || !res?.inviter) return;
        const name =
          res.inviter.firstName ||
          res.inviter.tetherHandle ||
          null;
        if (name) setInviterName(name);
        if (res.reason) setReason(res.reason);
      } catch {
        // non-fatal — fall back to generic "someone" / "no reason" copy
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediationId]);

  const initialMessages = useMemo(
    () => buildScript({ inviterName, reason }),
    [inviterName, reason],
  );

  const [messages, setMessages] = useState<ScriptMsg[]>(initialMessages);
  const [scriptBuilt, setScriptBuilt] = useState(false);

  // Re-build the script when the server fetch fills in inviterName / reason
  // (only before the user has interacted — once they tap a response or tell-me-
  // more, we freeze the script).
  useEffect(() => {
    if (scriptBuilt) return;
    setMessages(buildScript({ inviterName, reason }));
  }, [inviterName, reason, scriptBuilt]);

  const [tellMoreShown, setTellMoreShown] = useState(false);
  const [showTyping, setShowTyping] = useState(false);
  const [responding, setResponding] = useState<ResponseId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState<ResponseId | null>(null);

  useEffect(() => {
    // Scroll to bottom whenever the script grows.
    const el = document.querySelector('.medinv-messages');
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, showTyping]);

  const handleResponse = async (responseId: ResponseId) => {
    if (responding || completed) return;
    setError(null);
    // Freeze the script — don't let the server-fetch effect overwrite it now
    // that the user has started interacting.
    setScriptBuilt(true);

    if (responseId === 'tell_me_more') {
      if (tellMoreShown) return;
      setTellMoreShown(true);
      setMessages((prev) => [...prev, TELL_ME_MORE_USER]);
      setShowTyping(true);
      window.setTimeout(() => {
        setShowTyping(false);
        setMessages((prev) => [...prev, TELL_ME_MORE_F8]);
      }, 700);
      return;
    }

    if (!mediationId) {
      setError('missing mediation id');
      return;
    }

    setResponding(responseId);
    try {
      await respondToRoomInvite(mediationId, responseId);
      setCompleted(responseId);
      if (responseId === 'accept') {
        navigate(`/rooms/${mediationId}/prep`);
      } else {
        navigate('/rooms');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'could not record your response';
      setError(msg);
    } finally {
      setResponding(null);
    }
  };

  return (
    <div className="f8-chat-shell medinv-shell">
      <div className="f8-conversation">
        {/* Conv header — uses the f8 chat header styling */}
        <div className="f8-conv-header">
          <div className="f8-conv-header-left">
            <span className="f8-orb f8-orb-sm" />
            <span className="f8-conv-title">f8 · room invite</span>
          </div>
        </div>

        {/* Messages — scripted projection rendered with f8 chat bubble CSS */}
        <div className="f8-messages medinv-messages">
          <div className="f8-messages-inner">
            {messages.map((m) => (
              <div key={m.id} className="f8-msg-group">
                {m.sender === 'user' ? (
                  <div className="f8-user-bubble">
                    <span className="f8-msg-text">{m.text}</span>
                  </div>
                ) : (
                  <div className="f8-agent-wrap">
                    <div className="f8-agent-bubble-border">
                      <div className="f8-agent-bubble">
                        <span className="f8-msg-text">{m.text}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {showTyping && (
              <div className="f8-agent-wrap">
                <div className="f8-agent-bubble-border">
                  <div className="f8-agent-bubble f8-loading-bubble">
                    <span className="f8-loading-label">Thinking</span>
                    <div className="f8-loading-dots">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!!error && (
              <div className="medinv-error" role="alert">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Three equal-weight response buttons. All three render with the SAME
          CSS class (.medinv-response) — same height, padding, font-size, font
          weight. Only a tint differentiates accept from decline; nothing here
          is rendered as a primary "default" action. */}
      <div className="medinv-responses">
        {RESPONSES.map((r) => {
          const loading = responding === r.id;
          return (
            <button
              key={r.id}
              type="button"
              className={`medinv-response medinv-response-${r.id}`}
              onClick={() => handleResponse(r.id)}
              disabled={!!responding || !!completed}
            >
              {loading ? 'sending…' : r.label}
            </button>
          );
        })}
      </div>

      {/* Pre-signup composer — DISABLED. The F8Chat composer shell is
          rendered to signal "yes, this is f8" but the inputs are inert: the
          textarea is locked with a sign-off hint, the send button is
          inactive. There is no free-text channel for the invitee on this
          surface; only the three response buttons advance state. */}
      <div className="f8-composer-area medinv-composer-area">
        <div className="f8-composer-border">
          <div className="f8-composer-inner medinv-composer-inner">
            <div className="f8-composer-top">
              <textarea
                className="f8-textarea"
                placeholder="use the buttons above to respond"
                disabled
                readOnly
                rows={1}
                aria-label="composer disabled"
              />
            </div>
            <div className="f8-composer-bottom">
              <div className="f8-composer-right">
                <span className="f8-hint medinv-composer-hint">
                  free chat opens once you accept
                </span>
                <button
                  type="button"
                  className="f8-mic-btn"
                  disabled
                  aria-disabled="true"
                  title="disabled until you accept"
                >
                  <Mic size={16} />
                </button>
                <button
                  type="button"
                  className="f8-send-btn"
                  disabled
                  aria-disabled="true"
                  title="disabled until you accept"
                >
                  <ArrowUp size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomInvitationReceived;
